import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, FilePlus2, Fingerprint } from 'lucide-react'
import { useState } from 'react'

import { loanAccountQueryOptions } from '@/api/loans-api'
import { AuditTrailDrawer } from '@/components/audit-trail-drawer'
import { ErrorSurface } from '@/components/error-surface'
import { PageHeader } from '@/components/page-header'
import { StatusBadge } from '@/components/status-badge'
import { TabStrip, type TabDefinition } from '@/components/tab-strip'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  LoanAccountCyclesPanel,
  LoanAccountOverviewPanel,
} from '@/features/loans/account-panels'
import { LoanLink } from '@/features/loans/loan-link'
import { loanPaths, useLoanNavigate, useLoanParams, useRawLoanSearch } from '@/features/loans/loan-paths'
import { useLoanPermissions } from '@/features/loans/permissions'
import { LoanPrerequisitesPanel } from '@/features/loans/prerequisites-panel'

import { loanAccountDetailSearchSchema, parseLoanSearch, type LoanAccountTab } from './search'

const PANEL_ID = 'loan-account-panel'

/**
 * The loan account: the file, and the rounds written in it.
 *
 * Nothing here refuses a narrow display. Reading an account — what a member owes, whether they are
 * behind, which round is open — is exactly the question somebody rings the branch about, and the
 * answer is read far more often than it is acted on. Every action that would key an amount lives
 * on a screen of its own, and each of those refuses.
 */
export function LoanAccountDetailPage() {
  const params = useLoanParams()
  const accountId = params.accountId ?? ''
  const navigate = useLoanNavigate()
  const permissions = useLoanPermissions()
  const search = parseLoanSearch(loanAccountDetailSearchSchema, useRawLoanSearch())
  const [auditOpen, setAuditOpen] = useState(false)

  const query = useQuery(loanAccountQueryOptions(accountId))
  const tab: LoanAccountTab = search.tab ?? 'rounds'

  const selectTab = (next: LoanAccountTab) => {
    navigate(loanPaths.account(accountId), { search: { tab: next }, replace: true })
  }

  const applyAction = permissions.canApply ? (
    <Button asChild variant="primary" size="sm">
      <LoanLink to={loanPaths.apply(accountId)}>
        <FilePlus2 />
        Apply for a round
      </LoanLink>
    </Button>
  ) : null

  return (
    <>
      <LoanLink
        to={loanPaths.accounts}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-text hover:text-brand-text-hover max-lg:min-h-11 max-lg:py-2"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        All loans
      </LoanLink>

      {query.isPending ? (
        <div className="space-y-4">
          <Skeleton className="h-10 w-80" />
          <Skeleton className="h-9 w-full max-w-xl" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : query.isError ? (
        <ErrorSurface
          error={query.error}
          onRetry={() => void query.refetch()}
          title="This loan account could not be opened"
        />
      ) : (
        <>
          <PageHeader
            title={query.data.customerName}
            reference={query.data.accountNumber}
            description={`${query.data.productName} · ${query.data.totalCyclesCompleted} round${query.data.totalCyclesCompleted === 1 ? '' : 's'} completed`}
            badges={<StatusBadge status={query.data.status} />}
            actions={
              <>
                <Button variant="secondary" onClick={() => setAuditOpen(true)}>
                  <Fingerprint />
                  Audit trail
                </Button>
                {applyAction}
              </>
            }
          />

          <div className="space-y-4">
            <TabStrip<LoanAccountTab>
              tabs={
                [
                  { id: 'rounds', label: 'Rounds', badge: query.data.cycles.length },
                  { id: 'overview', label: 'Account' },
                  { id: 'prerequisites', label: 'Prerequisites' },
                ] satisfies TabDefinition<LoanAccountTab>[]
              }
              active={tab}
              onChange={selectTab}
              panelId={PANEL_ID}
              label="Loan account sections"
            />

            <div id={PANEL_ID} role="tabpanel" aria-labelledby={`${PANEL_ID}-tab-${tab}`}>
              {tab === 'rounds' ? (
                <LoanAccountCyclesPanel account={query.data} action={applyAction} />
              ) : null}

              {tab === 'overview' ? <LoanAccountOverviewPanel account={query.data} /> : null}

              {tab === 'prerequisites' ? (
                <LoanPrerequisitesPanel
                  customerId={query.data.customerId}
                  productId={query.data.productId}
                  title="Gates as they stand today"
                />
              ) : null}
            </div>
          </div>

          <AuditTrailDrawer
            open={auditOpen}
            onOpenChange={setAuditOpen}
            entityType="LoanAccount"
            entityId={query.data.id}
            subject={`${query.data.customerName} · ${query.data.accountNumber}`}
          />
        </>
      )}
    </>
  )
}
